import { useState } from "react";
import { Btn } from "@/components/Btn";
import type { DashboardView } from "@/lib/types";

// Confirmation modal for deleting a saved dashboard view. Replaces the
// native `confirm()` dialog — same shell as SaveViewDialog (overlay +
// card + Btn actions) so it fits the rest of the app visually and
// can't be accidentally dismissed by a single Enter keypress.
export function DeleteViewConfirm({
  view,
  onCancel,
  onConfirm,
}: {
  view: DashboardView;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      // Parent unmounts us on success; on failure we stay mounted
      // and re-enable the button so the user can retry or cancel.
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14,18,32,0.45)",
        zIndex: 100,
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div
        className="card"
        style={{
          width: "100%",
          maxWidth: 420,
          padding: 20,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>Delete dashboard view?</h3>
        <p style={{ fontSize: 13, marginTop: 10, marginBottom: 0 }}>
          <b>“{view.name}”</b>
          {view.visibility === "shared" ? (
            <span className="muted"> — shared with your organization.</span>
          ) : (
            <span className="muted"> — private view.</span>
          )}
        </p>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          This can&apos;t be undone. Any saved filters and layout will be lost.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 18,
          }}
        >
          <Btn type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </Btn>
          <Btn
            variant="danger"
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting}
          >
            {submitting ? "Deleting…" : "Delete"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
